/**
 * 文本高亮指令
 * @directive 用法：v-highlight="'关键词'"
 * @directive 用法：v-highlight="{ keyword: '关键词', className: 'my-highlight' }"
 * @directive 也支持正则字符串：v-highlight="{ pattern: '\\d+', flags: 'g', className: 'highlight' }"
 */
export default {
  mounted(el, binding) {
    doHighlight(el, binding.value)
  },
  updated(el, binding) {
    // 先清除旧高亮，再重新执行
    doHighlight(el, binding.value)
  },
  unmounted(el) {
    // 卸载时清理高亮（可选）
    removeHighlight(el)
  }
}

// ---------- 内部实现 ----------

/** 默认高亮样式类名 */
const DEFAULT_CLASS = "highlight"

/**
 * 执行高亮逻辑
 */
function doHighlight(el, value) {
  // 先移除之前的标注，恢复原始文本节点
  removeHighlight(el)

  // 解析参数
  const config = resolveConfig(value)
  if (!config) return

  // 构建正则
  let regex
  try {
    regex = buildRegex(config)
  } catch {
    return // 正则不合法时静默失败
  }
  if (!regex) return

  // 遍历文本节点并高亮
  walkTextNodes(el, (textNode) => {
    const text = textNode.textContent
    if (!regex.test(text)) return
    regex.lastIndex = 0

    const fragment = document.createDocumentFragment()
    let lastIndex = 0
    let match
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
      }
      const mark = document.createElement("mark")
      mark.className = config.className || DEFAULT_CLASS
      mark.textContent = match[0]
      fragment.appendChild(mark)
      lastIndex = regex.lastIndex
      if (match[0].length === 0) regex.lastIndex++ // 防止零宽匹配死循环
    }
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)))
    }
    textNode.parentNode?.replaceChild(fragment, textNode)
  })
}

/**
 * 移除所有已生成的高亮 <mark> 标签，恢复原始文本节点
 */
function removeHighlight(el) {
  const marks = el.querySelectorAll("mark")
  // 从深层往浅层替换，避免结构变化导致的遍历问题
  for (const mark of marks) {
    const parent = mark.parentNode
    if (parent) {
      parent.replaceChild(document.createTextNode(mark.textContent || ""), mark)
      // 合并相邻的文本节点（可选，为了更干净）
      parent.normalize()
    }
  }
}

/**
 * 将 binding.value 统一解析为配置对象
 */
function resolveConfig(value) {
  if (!value) return null
  // 纯字符串：视为关键词
  if (typeof value === "string") {
    return { keyword: value }
  }
  // 对象形式
  if (typeof value === "object") {
    return value
  }
  return null
}

/**
 * 根据配置构建正则表达式
 */
function buildRegex(config) {
  // 优先使用 pattern（正则表达式字符串）
  if (config.pattern) {
    const flags = config.flags || "gi"
    return new RegExp(`(${config.pattern})`, flags)
  }
  // 使用 keyword（普通字符串，需转义）
  if (config.keyword) {
    const escaped = config.keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const flags = config.flags || "gi"
    return new RegExp(`(${escaped})`, flags)
  }
  return null
}

/**
 * 深度遍历 DOM 树，对所有文本节点执行回调
 */
function walkTextNodes(node, callback) {
  // 跳过已被高亮的 <mark> 以及 script/style
  if (node.nodeType === Node.TEXT_NODE) {
    callback(node)
    return
  }
  if (node.nodeType !== Node.ELEMENT_NODE || node.tagName === "MARK" || node.tagName === "SCRIPT" || node.tagName === "STYLE") {
    return
  }
  // 使用 childNodes 的静态副本，防止遍历时节点被替换
  const children = Array.from(node.childNodes)
  children.forEach((child) => walkTextNodes(child, callback))
}
